/**
 * useVoiceAgent — OpenAI Realtime API voice conversation hook
 * Mic → NoiseGate → WebSocket → OpenAI → Audio playback
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { getApiBaseUrl } from '../utils/apiBase';

const WS_URL = getApiBaseUrl().replace(/^http/, 'ws') + '/voice-agent';
const TARGET_RATE = 24000; // OpenAI expects 24kHz


function resampleTo24k(input, inputRate) {
  if (inputRate === TARGET_RATE) return input;
  const ratio = inputRate / TARGET_RATE;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const srcIdx = i * ratio;
    const lo = Math.floor(srcIdx);
    const hi = Math.min(lo + 1, input.length - 1);
    const frac = srcIdx - lo;
    out[i] = input[lo] * (1 - frac) + input[hi] * frac;
  }
  return out;
}

function floatTo16BitPCM(float32Array) {
  const buffer = new ArrayBuffer(float32Array.length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}

function base64EncodePCM(pcmArrayBuffer) {
  const bytes = new Uint8Array(pcmArrayBuffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function useVoiceAgent() {
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [messages, setMessages] = useState([]);
  const [noiseLevel, setNoiseLevel] = useState(0);
  const [error, setError] = useState('');

  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const processorRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const transcriptRef = useRef('');

  const playNextChunk = useCallback(() => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      setIsSpeaking(false);
      return;
    }
    isPlayingRef.current = true;
    setIsSpeaking(true);
    const chunk = audioQueueRef.current.shift();
    const ctx = audioContextRef.current;
    if (!ctx) return;
    const buffer = ctx.createBuffer(1, chunk.length, TARGET_RATE);
    buffer.getChannelData(0).set(chunk);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.onended = () => playNextChunk();
    source.start();
  }, []);

  useEffect(() => {
    return () => {
      wsRef.current?.close();
      streamRef.current?.getTracks().forEach(t => t.stop());
      audioContextRef.current?.close();
    };
  }, []);

  const connect = useCallback(async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ctx;
      await ctx.resume();
      const inputRate = ctx.sampleRate;

      const source = ctx.createMediaStreamSource(stream);
      const hpf = ctx.createBiquadFilter();
      hpf.type = 'highpass';
      hpf.frequency.value = 80;
      const lpf = ctx.createBiquadFilter();
      lpf.type = 'lowpass';
      lpf.frequency.value = 8000;
      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.value = -24;
      compressor.knee.value = 30;
      compressor.ratio.value = 12;
      compressor.attack.value = 0.003;
      compressor.release.value = 0.25;

      const bufferSize = 4096;
      const processor = ctx.createScriptProcessor(bufferSize, 1, 1);
      processorRef.current = processor;

      source.connect(hpf);
      hpf.connect(lpf);
      lpf.connect(compressor);
      compressor.connect(processor);
      processor.connect(ctx.destination);

      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setIsListening(true);
        processor.onaudioprocess = (e) => {
          const input = e.inputBuffer.getChannelData(0);
          const rms = Math.sqrt(input.reduce((s, x) => s + x * x, 0) / input.length);
          setNoiseLevel(Math.min(100, rms * 500));
          if (!ws || ws.readyState !== WebSocket.OPEN) return;
          const resampled = resampleTo24k(input, inputRate);
          const pcm = floatTo16BitPCM(resampled);
          const b64 = base64EncodePCM(pcm);
          ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: b64 }));
        };
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const t = data.type;
          if (t === 'session.created' || t === 'session.updated') return;
          if (t === 'conversation.item.input_audio_transcription.completed') {
            transcriptRef.current = data.transcript || '';
            setMessages(prev => [...prev, { role: 'user', content: data.transcript }]);
            return;
          }
          if (t === 'response.output_audio.delta' && data.delta) {
            const bytes = Uint8Array.from(atob(data.delta), c => c.charCodeAt(0));
            const samples = new Int16Array(bytes.buffer);
            const float32 = new Float32Array(samples.length);
            for (let i = 0; i < samples.length; i++) {
              float32[i] = samples[i] / 32768;
            }
            audioQueueRef.current.push(float32);
            if (!isPlayingRef.current) playNextChunk();
            return;
          }
          if (t === 'response.output_audio_transcript.delta' && data.delta) {
            setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant') {
                return [...prev.slice(0, -1), { ...last, content: (last.content || '') + data.delta }];
              }
              return [...prev, { role: 'assistant', content: data.delta }];
            });
            return;
          }
          if (t === 'response.output_audio_transcript.done' && data.transcript) {
            setMessages(prev => {
              const filtered = prev.filter(m => m.role !== 'assistant' || m.content);
              return [...filtered, { role: 'assistant', content: data.transcript }];
            });
            return;
          }
          if (t === 'error') {
            setError(data.error?.message || 'Voice Agent error');
          }
        } catch (err) {
          console.error('Voice Agent parse error:', err);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        setIsListening(false);
        processor.onaudioprocess = null;
      };
      ws.onerror = () => setError('WebSocket connection failed');
    } catch (err) {
      setError(err.message || 'Microphone access denied');
    }
  }, [playNextChunk]);

  const disconnect = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (processorRef.current) {
      processorRef.current.onaudioprocess = null;
    }
    setIsConnected(false);
    setIsListening(false);
  }, []);

  const cancelResponse = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'response.cancel' }));
    }
  }, []);

  return {
    connect,
    disconnect,
    cancelResponse,
    isConnected,
    isListening: isConnected && isListening,
    isSpeaking,
    messages,
    noiseLevel,
    error,
  };
}
